import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, FilePlus2Icon, FileTextIcon, InfoIcon } from 'lucide-react';
import { originalLoanDocuments, reAppLenders } from '../../data/reApp';
import { loanHeader } from '../../data/withdraw';
import { DeadForm } from '../../types/withdraw';
import { inputClasses } from '../../utils/styles';
import { Expand } from '../ui/Expand';
import { FormField } from '../ui/FormField';
interface ReAppSectionProps {
  form: DeadForm;
  currentLender: string;
  onChange: (patch: Partial<DeadForm>) => void;
}
export function ReAppSection({
  form,
  currentLender,
  onChange
}: ReAppSectionProps) {
  const firstName = loanHeader.borrower.split(' ')[0];
  const lenders = reAppLenders.filter(l => l !== currentLender);
  const total = originalLoanDocuments.length;
  const selectedCount = form.carryDocs.length;
  const allSelected = selectedCount === total;
  const toggleDoc = (id: string) => onChange({
    carryDocs: form.carryDocs.includes(id) ? form.carryDocs.filter(d => d !== id) : [...form.carryDocs, id]
  });
  return <div className={`rounded-2xl border p-4 transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft ${form.reApp ? 'border-primary-200 bg-primary-50/40 shadow-soft' : 'border-slate-200 bg-white'}`} data-id="element-131" data-mp="1mtvc2gysnubw">
      <div className="flex items-start gap-4" data-id="element-132" data-mp="1d1pnlh7r3v1g">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-100" data-id="element-133" data-mp="20t4ulnah30j1">
          <FilePlus2Icon className="h-5 w-5" aria-hidden="true" data-id="element-134" data-mp="25rptnjdzne5w" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-135" data-mp="1quf81h7msrwg">
          <p id="reapp-label" className="text-sm font-semibold text-slate-900" data-id="element-136" data-mp="1qxkwqvcclrrn">
            Re-App the loan
          </p>
          <p className="mt-0.5 text-sm text-slate-600" data-id="element-137" data-mp="fgbjiqjtbvys">
            Start {firstName}'s application with the new lender without re-entering her details.
          </p>
        </div>
        <button type="button" role="switch" aria-checked={form.reApp} aria-labelledby="reapp-label" onClick={() => onChange({
        reApp: !form.reApp
      })} className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ease-out-soft focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${form.reApp ? 'bg-primary-700' : 'bg-slate-300'}`} data-id="element-138" data-mp="2n1tr8mjawmj1">
          <motion.span initial={false} animate={{
          x: form.reApp ? 22 : 2
        }} transition={{
          type: 'spring',
          stiffness: 600,
          damping: 34
        }} className="absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow-soft" data-id="element-139" data-mp="23bq19xle0ty1" />
        </button>
      </div>

      <Expand show={form.reApp} data-id="element-140" data-mp="1pg5vm3uo688k">
        <div className="space-y-5 pb-1 pt-5" data-id="element-141" data-mp="3oqxa5ay43otj">
          <div className="flex gap-3 rounded-xl bg-accent-50 px-3.5 py-3 ring-1 ring-accent-100" data-id="element-142" data-mp="3oqnh1ehrt1jm">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-600" aria-hidden="true" data-id="element-143" data-mp="3jslwep4p4u61" />
            <p className="text-sm text-slate-700" data-id="element-144" data-mp="3rb2806w81dp3">
              <span className="font-semibold text-slate-900" data-id="element-145" data-mp="50ttpgym3z8">Re-Apping creates a new loan record.</span> Borrower, property, and
              loan details are brought over. This file is still withdrawn.
            </p>
          </div>

          <FormField label="New lender" htmlFor="reapp-lender" required className="sm:max-w-[320px]" data-id="element-146" data-mp="9tsgbi393w2r">
            <div className="relative" data-id="element-147" data-mp="38e0zic3gkvge">
              <select id="reapp-lender" value={form.newLender} onChange={e => onChange({
              newLender: e.target.value
            })} className={`${inputClasses} appearance-none pr-10 ${form.newLender ? '' : 'text-slate-400'}`} data-id="element-148" data-mp="38f3ryogfj5yf">
                <option value="" disabled data-id="element-149" data-mp="3n7ngaiyx78vt">
                  Select a lender
                </option>
                {lenders.map(l => <option key={l} value={l} className="text-slate-900" data-id="element-150" data-mp="12izoz9wf621q">
                    {l}
                  </option>)}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-151" data-mp="w3ipyrcnoen4" />
            </div>
          </FormField>

          <fieldset data-id="element-152" data-mp="1a0xk29dutalf">
            <div className="mb-2 flex items-center justify-between gap-3" data-id="element-153" data-mp="154gurbux4q6r">
              <legend className="text-[13px] font-medium text-slate-700" data-id="element-154" data-mp="156jw2s1iu5qs">
                Bring documents over <span className="font-normal text-slate-500" data-id="element-155" data-mp="1j1vt5635g8av">· optional</span>
              </legend>
              <div className="flex items-center gap-3 text-xs" data-id="element-156" data-mp="1f0af3hevpnz3">
                <span className="tabular-nums text-slate-500" aria-live="polite" data-id="element-157" data-mp="1ssgnghbsiqnz">
                  {selectedCount} of {total} selected
                </span>
                <button type="button" onClick={() => onChange({
                carryDocs: allSelected ? [] : originalLoanDocuments.map(d => d.id)
              })} className="rounded-full px-2 py-0.5 font-semibold text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200" data-id="element-158" data-mp="22rpn9qtc6zi9">
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2" data-id="element-159" data-mp="2ceiidsazbj8k">
              {originalLoanDocuments.map(doc => {
              const checked = form.carryDocs.includes(doc.id);
              return <label key={doc.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border bg-white px-3 py-2.5 transition-[border-color,box-shadow] duration-150 ease-out-soft ${checked ? 'border-primary-500 ring-2 ring-primary-100' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-160" data-mp="22ifd4dktz4he">
                    <input type="checkbox" className="peer sr-only" checked={checked} onChange={() => toggleDoc(doc.id)} data-id="element-161" data-mp="1jpkzbmyy1ier" />
                    <span aria-hidden="true" className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border-2 transition-colors duration-150 peer-focus-visible:ring-4 peer-focus-visible:ring-primary-100 ${checked ? 'border-primary-700 bg-primary-700' : 'border-slate-300 bg-white'}`} data-id="element-162" data-mp="10nlx9lw33gxh">
                      {checked && <motion.span initial={{
                    scale: 0.6,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-163" data-mp="1eliumkid3rd7">
                          <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-164" data-mp="19lecyr5pwqle" />
                        </motion.span>}
                    </span>
                    <FileTextIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-165" data-mp="3ue9ekcrkv26n" />
                    <span className="min-w-0" data-id="element-166" data-mp="3pd26ecliw0zg">
                      <span className="block truncate text-sm font-medium text-slate-800" data-id="element-167" data-mp="3pggabp9u4i90">{doc.name}</span>
                      <span className="block truncate text-xs text-slate-500" data-id="element-168" data-mp="24u88fc5oc2ny">{doc.meta}</span>
                    </span>
                  </label>;
            })}
            </div>
          </fieldset>
        </div>
      </Expand>
    </div>;
}